import { useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '@/shared/i18n/useT';
import { DEVICE_SCOPES, type DeviceScope } from './device';

const ICONS: Record<DeviceScope, ReactElement> = {
  all: (
    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="14" height="10" rx="1.5" />
      <path d="M6 18h6" />
      <rect x="16" y="9" width="6" height="11" rx="1.5" />
    </svg>
  ),
  desktop: (
    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  ),
  mobile: (
    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="2" width="12" height="20" rx="2" />
      <path d="M11 18h2" />
    </svg>
  ),
};

/** The icon of one device (or of all of them), for menus that name a device. */
export function DeviceIcon({ device }: { readonly device: DeviceScope }) {
  return ICONS[device];
}

interface Props {
  readonly value: DeviceScope;
  readonly onChange: (scope: DeviceScope) => void;
  /** What the choice scopes, for screen readers: "Show Price on". */
  readonly label?: string;
  readonly disabled?: boolean;
  readonly className?: string;
}

/**
 * Three icon segments — all devices, desktop, mobile — saying where one layout
 * choice applies. Every segment explains itself in a tooltip on hover or focus;
 * the tooltip is portaled so a clipped sidebar or menu never cuts it off.
 */
export function DeviceSwitch({ value, onChange, label, disabled = false, className = '' }: Props) {
  const t = useT();
  const [tip, setTip] = useState<{ text: string; left: number; top: number } | null>(null);

  const showTip = (el: HTMLElement, text: string) => {
    const rect = el.getBoundingClientRect();
    setTip({ text, left: rect.left + rect.width / 2, top: rect.top });
  };

  return (
    <div role="radiogroup" aria-label={label ?? t('device.label')}
      className={`inline-flex shrink-0 items-center overflow-hidden rounded-full border border-border bg-surface p-0.5 ${disabled ? 'opacity-50' : ''} ${className}`}>
      {DEVICE_SCOPES.map((scope) => {
        const selected = value === scope;
        const text = t(`device.${scope}`);
        return (
          <button key={scope} type="button" role="radio" aria-checked={selected} aria-label={text} disabled={disabled}
            onClick={(event) => { event.stopPropagation(); if (!selected) onChange(scope); }}
            onMouseEnter={(event) => showTip(event.currentTarget, text)}
            onMouseLeave={() => setTip(null)}
            onFocus={(event) => showTip(event.currentTarget, text)}
            onBlur={() => setTip(null)}
            className={`flex h-6 w-6 items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed ${selected ? 'bg-brand/10 text-brand' : 'text-text-muted hover:bg-surface-hover hover:text-text'}`}>
            {ICONS[scope]}
          </button>
        );
      })}
      {tip && createPortal(
        <span role="tooltip" style={{ left: tip.left, top: tip.top - 6 }}
          className="pointer-events-none fixed z-[200] -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md bg-text px-2 py-1 text-xs font-medium text-bg shadow-card">
          {tip.text}
        </span>,
        document.body,
      )}
    </div>
  );
}
